/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `segment_lap` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class SegmentLapMesg : Mesg {
    public constructor() : super("SegmentLap", Profile.MesgNum.SEGMENT_LAP)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** event. */
    public var event: Event?
        get() = (getFieldValue(EVENT_FIELD_NUM) as? UByte)?.let { Event.fromValue(it) }
        set(value) = setFieldValue(EVENT_FIELD_NUM, value?.value)

    /** event_type. */
    public var eventType: EventType?
        get() = (getFieldValue(EVENT_TYPE_FIELD_NUM) as? UByte)?.let { EventType.fromValue(it) }
        set(value) = setFieldValue(EVENT_TYPE_FIELD_NUM, value?.value)

    /** start_time. */
    public var startTime: Instant?
        get() = (getFieldValue(START_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** start_position_lat, in semicircles. */
    public var startPositionLat: Int?
        get() = getFieldValue(START_POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(START_POSITION_LAT_FIELD_NUM, value)

    /** start_position_long, in semicircles. */
    public var startPositionLong: Int?
        get() = getFieldValue(START_POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(START_POSITION_LONG_FIELD_NUM, value)

    /** end_position_lat, in semicircles. */
    public var endPositionLat: Int?
        get() = getFieldValue(END_POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(END_POSITION_LAT_FIELD_NUM, value)

    /** end_position_long, in semicircles. */
    public var endPositionLong: Int?
        get() = getFieldValue(END_POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(END_POSITION_LONG_FIELD_NUM, value)

    /** total_elapsed_time, in s. Time (includes pauses) */
    public var totalElapsedTime: Double?
        get() = getFieldValue(TOTAL_ELAPSED_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_ELAPSED_TIME_FIELD_NUM, value)

    /** total_timer_time, in s. Timer Time (excludes pauses) */
    public var totalTimerTime: Double?
        get() = getFieldValue(TOTAL_TIMER_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_TIMER_TIME_FIELD_NUM, value)

    /** total_distance, in m. */
    public var totalDistance: Double?
        get() = getFieldValue(TOTAL_DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_DISTANCE_FIELD_NUM, value)

    /** total_cycles, in cycles. */
    public var totalCycles: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TOTAL_CYCLES_FIELD_NUM, value)

    /**
     * total_strokes: what total_cycles means when this message selects it.
     */
    public val totalStrokes: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalStrokes")) as? UInt

    public fun setTotalStrokes(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalStrokes"))
    }

    /** total_calories, in kcal. */
    public var totalCalories: UShort?
        get() = getFieldValue(TOTAL_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_CALORIES_FIELD_NUM, value)

    /** total_fat_calories, in kcal. If New Leaf */
    public var totalFatCalories: UShort?
        get() = getFieldValue(TOTAL_FAT_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_FAT_CALORIES_FIELD_NUM, value)

    /** avg_speed, in m/s. */
    public var avgSpeed: Double?
        get() = getFieldValue(AVG_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_SPEED_FIELD_NUM, value)

    /** max_speed, in m/s. */
    public var maxSpeed: Double?
        get() = getFieldValue(MAX_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_SPEED_FIELD_NUM, value)

    /** avg_heart_rate, in bpm. */
    public var avgHeartRate: UByte?
        get() = getFieldValue(AVG_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AVG_HEART_RATE_FIELD_NUM, value)

    /** max_heart_rate, in bpm. */
    public var maxHeartRate: UByte?
        get() = getFieldValue(MAX_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MAX_HEART_RATE_FIELD_NUM, value)

    /** avg_cadence, in rpm. total_cycles / total_timer_time if non_zero_avg_cadence otherwise total_cycles / total_elapsed_time */
    public var avgCadence: UByte?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AVG_CADENCE_FIELD_NUM, value)

    /** max_cadence, in rpm. */
    public var maxCadence: UByte?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MAX_CADENCE_FIELD_NUM, value)

    /** avg_power, in watts. total_power / total_timer_time if non_zero_avg_power otherwise total_power / total_elapsed_time */
    public var avgPower: UShort?
        get() = getFieldValue(AVG_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_POWER_FIELD_NUM, value)

    /** max_power, in watts. */
    public var maxPower: UShort?
        get() = getFieldValue(MAX_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MAX_POWER_FIELD_NUM, value)

    /** total_ascent, in m. */
    public var totalAscent: UShort?
        get() = getFieldValue(TOTAL_ASCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_ASCENT_FIELD_NUM, value)

    /** total_descent, in m. */
    public var totalDescent: UShort?
        get() = getFieldValue(TOTAL_DESCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_DESCENT_FIELD_NUM, value)

    /** sport. */
    public var sport: Sport?
        get() = (getFieldValue(SPORT_FIELD_NUM) as? UByte)?.let { Sport.fromValue(it) }
        set(value) = setFieldValue(SPORT_FIELD_NUM, value?.value)

    /** event_group. */
    public var eventGroup: UByte?
        get() = getFieldValue(EVENT_GROUP_FIELD_NUM) as? UByte
        set(value) = setFieldValue(EVENT_GROUP_FIELD_NUM, value)

    /** nec_lat, in semicircles. North east corner latitude. */
    public var necLat: Int?
        get() = getFieldValue(NEC_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(NEC_LAT_FIELD_NUM, value)

    /** nec_long, in semicircles. North east corner longitude. */
    public var necLong: Int?
        get() = getFieldValue(NEC_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(NEC_LONG_FIELD_NUM, value)

    /** swc_lat, in semicircles. South west corner latitude. */
    public var swcLat: Int?
        get() = getFieldValue(SWC_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(SWC_LAT_FIELD_NUM, value)

    /** swc_long, in semicircles. South west corner latitude. */
    public var swcLong: Int?
        get() = getFieldValue(SWC_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(SWC_LONG_FIELD_NUM, value)

    /** name. */
    public var name: String?
        get() = getFieldValue(NAME_FIELD_NUM) as? String
        set(value) = setFieldValue(NAME_FIELD_NUM, value)

    /** normalized_power, in watts. */
    public var normalizedPower: UShort?
        get() = getFieldValue(NORMALIZED_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NORMALIZED_POWER_FIELD_NUM, value)

    /** left_right_balance. */
    public var leftRightBalance: UShort?
        get() = getFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM, value)

    /** sub_sport. */
    public var subSport: SubSport?
        get() = (getFieldValue(SUB_SPORT_FIELD_NUM) as? UByte)?.let { SubSport.fromValue(it) }
        set(value) = setFieldValue(SUB_SPORT_FIELD_NUM, value?.value)

    /** total_work, in J. */
    public var totalWork: UInt?
        get() = getFieldValue(TOTAL_WORK_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TOTAL_WORK_FIELD_NUM, value)

    /** avg_altitude, in m. */
    public var avgAltitude: Double?
        get() = getFieldValue(AVG_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_ALTITUDE_FIELD_NUM, value)

    /** max_altitude, in m. */
    public var maxAltitude: Double?
        get() = getFieldValue(MAX_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_ALTITUDE_FIELD_NUM, value)

    /** gps_accuracy, in m. */
    public var gpsAccuracy: UByte?
        get() = getFieldValue(GPS_ACCURACY_FIELD_NUM) as? UByte
        set(value) = setFieldValue(GPS_ACCURACY_FIELD_NUM, value)

    /** avg_grade, in %. */
    public var avgGrade: Double?
        get() = getFieldValue(AVG_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_GRADE_FIELD_NUM, value)

    /** avg_pos_grade, in %. */
    public var avgPosGrade: Double?
        get() = getFieldValue(AVG_POS_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_POS_GRADE_FIELD_NUM, value)

    /** avg_neg_grade, in %. */
    public var avgNegGrade: Double?
        get() = getFieldValue(AVG_NEG_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_NEG_GRADE_FIELD_NUM, value)

    /** max_pos_grade, in %. */
    public var maxPosGrade: Double?
        get() = getFieldValue(MAX_POS_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_POS_GRADE_FIELD_NUM, value)

    /** max_neg_grade, in %. */
    public var maxNegGrade: Double?
        get() = getFieldValue(MAX_NEG_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_NEG_GRADE_FIELD_NUM, value)

    /** avg_temperature, in C. */
    public var avgTemperature: Byte?
        get() = getFieldValue(AVG_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_TEMPERATURE_FIELD_NUM, value)

    /** max_temperature, in C. */
    public var maxTemperature: Byte?
        get() = getFieldValue(MAX_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(MAX_TEMPERATURE_FIELD_NUM, value)

    /** total_moving_time, in s. */
    public var totalMovingTime: Double?
        get() = getFieldValue(TOTAL_MOVING_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_MOVING_TIME_FIELD_NUM, value)

    /** avg_pos_vertical_speed, in m/s. */
    public var avgPosVerticalSpeed: Double?
        get() = getFieldValue(AVG_POS_VERTICAL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_POS_VERTICAL_SPEED_FIELD_NUM, value)

    /** avg_neg_vertical_speed, in m/s. */
    public var avgNegVerticalSpeed: Double?
        get() = getFieldValue(AVG_NEG_VERTICAL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_NEG_VERTICAL_SPEED_FIELD_NUM, value)

    /** max_pos_vertical_speed, in m/s. */
    public var maxPosVerticalSpeed: Double?
        get() = getFieldValue(MAX_POS_VERTICAL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_POS_VERTICAL_SPEED_FIELD_NUM, value)

    /** max_neg_vertical_speed, in m/s. */
    public var maxNegVerticalSpeed: Double?
        get() = getFieldValue(MAX_NEG_VERTICAL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_NEG_VERTICAL_SPEED_FIELD_NUM, value)

    /** Number of values stored in time_in_hr_zone. */
    public val numTimeInHrZone: Int
        get() = getField(TIME_IN_HR_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_hr_zone, in s. */
    public var timeInHrZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_HR_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInHrZone(it) }
        }
        set(value) {
            removeField(TIME_IN_HR_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInHrZone(index, element) }
        }

    public fun getTimeInHrZone(index: Int): Double? =
        getFieldValue(TIME_IN_HR_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInHrZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_HR_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_in_speed_zone. */
    public val numTimeInSpeedZone: Int
        get() = getField(TIME_IN_SPEED_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_speed_zone, in s. */
    public var timeInSpeedZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_SPEED_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInSpeedZone(it) }
        }
        set(value) {
            removeField(TIME_IN_SPEED_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInSpeedZone(index, element) }
        }

    public fun getTimeInSpeedZone(index: Int): Double? =
        getFieldValue(TIME_IN_SPEED_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInSpeedZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_SPEED_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_in_cadence_zone. */
    public val numTimeInCadenceZone: Int
        get() = getField(TIME_IN_CADENCE_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_cadence_zone, in s. */
    public var timeInCadenceZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_CADENCE_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInCadenceZone(it) }
        }
        set(value) {
            removeField(TIME_IN_CADENCE_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInCadenceZone(index, element) }
        }

    public fun getTimeInCadenceZone(index: Int): Double? =
        getFieldValue(TIME_IN_CADENCE_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInCadenceZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_CADENCE_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_in_power_zone. */
    public val numTimeInPowerZone: Int
        get() = getField(TIME_IN_POWER_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_power_zone, in s. */
    public var timeInPowerZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_POWER_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInPowerZone(it) }
        }
        set(value) {
            removeField(TIME_IN_POWER_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInPowerZone(index, element) }
        }

    public fun getTimeInPowerZone(index: Int): Double? =
        getFieldValue(TIME_IN_POWER_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInPowerZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_POWER_ZONE_FIELD_NUM, value, index)
    }

    /** repetition_num. */
    public var repetitionNum: UShort?
        get() = getFieldValue(REPETITION_NUM_FIELD_NUM) as? UShort
        set(value) = setFieldValue(REPETITION_NUM_FIELD_NUM, value)

    /** min_altitude, in m. */
    public var minAltitude: Double?
        get() = getFieldValue(MIN_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MIN_ALTITUDE_FIELD_NUM, value)

    /** min_heart_rate, in bpm. */
    public var minHeartRate: UByte?
        get() = getFieldValue(MIN_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MIN_HEART_RATE_FIELD_NUM, value)

    /** active_time, in s. */
    public var activeTime: Double?
        get() = getFieldValue(ACTIVE_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(ACTIVE_TIME_FIELD_NUM, value)

    /** wkt_step_index. */
    public var wktStepIndex: UShort?
        get() = getFieldValue(WKT_STEP_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(WKT_STEP_INDEX_FIELD_NUM, value)

    /** sport_event. */
    public var sportEvent: SportEvent?
        get() = (getFieldValue(SPORT_EVENT_FIELD_NUM) as? UByte)?.let { SportEvent.fromValue(it) }
        set(value) = setFieldValue(SPORT_EVENT_FIELD_NUM, value?.value)

    /** avg_left_torque_effectiveness, in percent. */
    public var avgLeftTorqueEffectiveness: Double?
        get() = getFieldValue(AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** avg_right_torque_effectiveness, in percent. */
    public var avgRightTorqueEffectiveness: Double?
        get() = getFieldValue(AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** avg_left_pedal_smoothness, in percent. */
    public var avgLeftPedalSmoothness: Double?
        get() = getFieldValue(AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_right_pedal_smoothness, in percent. */
    public var avgRightPedalSmoothness: Double?
        get() = getFieldValue(AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_combined_pedal_smoothness, in percent. */
    public var avgCombinedPedalSmoothness: Double?
        get() = getFieldValue(AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** status. */
    public var status: SegmentLapStatus?
        get() = (getFieldValue(STATUS_FIELD_NUM) as? UByte)?.let { SegmentLapStatus.fromValue(it) }
        set(value) = setFieldValue(STATUS_FIELD_NUM, value?.value)

    /** uuid. */
    public var uuid: String?
        get() = getFieldValue(UUID_FIELD_NUM) as? String
        set(value) = setFieldValue(UUID_FIELD_NUM, value)

    /** avg_fractional_cadence, in rpm. fractional part of the avg_cadence */
    public var avgFractionalCadence: Double?
        get() = getFieldValue(AVG_FRACTIONAL_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_FRACTIONAL_CADENCE_FIELD_NUM, value)

    /** max_fractional_cadence, in rpm. fractional part of the max_cadence */
    public var maxFractionalCadence: Double?
        get() = getFieldValue(MAX_FRACTIONAL_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_FRACTIONAL_CADENCE_FIELD_NUM, value)

    /** total_fractional_cycles, in cycles. fractional part of the total_cycles */
    public var totalFractionalCycles: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_CYCLES_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_CYCLES_FIELD_NUM, value)

    /** front_gear_shift_count. */
    public var frontGearShiftCount: UShort?
        get() = getFieldValue(FRONT_GEAR_SHIFT_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(FRONT_GEAR_SHIFT_COUNT_FIELD_NUM, value)

    /** rear_gear_shift_count. */
    public var rearGearShiftCount: UShort?
        get() = getFieldValue(REAR_GEAR_SHIFT_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(REAR_GEAR_SHIFT_COUNT_FIELD_NUM, value)

    /** time_standing, in s. Total time spent in the standing position */
    public var timeStanding: Double?
        get() = getFieldValue(TIME_STANDING_FIELD_NUM) as? Double
        set(value) = setFieldValue(TIME_STANDING_FIELD_NUM, value)

    /** stand_count. Number of transitions to the standing state */
    public var standCount: UShort?
        get() = getFieldValue(STAND_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(STAND_COUNT_FIELD_NUM, value)

    /** avg_left_pco, in mm. Average left platform center offset */
    public var avgLeftPco: Byte?
        get() = getFieldValue(AVG_LEFT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_LEFT_PCO_FIELD_NUM, value)

    /** avg_right_pco, in mm. Average right platform center offset */
    public var avgRightPco: Byte?
        get() = getFieldValue(AVG_RIGHT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_RIGHT_PCO_FIELD_NUM, value)

    /** Number of values stored in avg_left_power_phase. */
    public val numAvgLeftPowerPhase: Int
        get() = getField(AVG_LEFT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** avg_left_power_phase, in degrees. Average left power phase angles. Data value indexes defined by power_phase_type. */
    public var avgLeftPowerPhase: List<Double?>?
        get() {
            val field = getField(AVG_LEFT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgLeftPowerPhase(it) }
        }
        set(value) {
            removeField(AVG_LEFT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgLeftPowerPhase(index, element) }
        }

    public fun getAvgLeftPowerPhase(index: Int): Double? =
        getFieldValue(AVG_LEFT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setAvgLeftPowerPhase(index: Int, value: Double?) {
        setFieldValue(AVG_LEFT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_left_power_phase_peak. */
    public val numAvgLeftPowerPhasePeak: Int
        get() = getField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** avg_left_power_phase_peak, in degrees. Average left power phase peak angles. Data value indexes defined by power_phase_type. */
    public var avgLeftPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgLeftPowerPhasePeak(it) }
        }
        set(value) {
            removeField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgLeftPowerPhasePeak(index, element) }
        }

    public fun getAvgLeftPowerPhasePeak(index: Int): Double? =
        getFieldValue(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setAvgLeftPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_right_power_phase. */
    public val numAvgRightPowerPhase: Int
        get() = getField(AVG_RIGHT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** avg_right_power_phase, in degrees. Average right power phase angles. Data value indexes defined by power_phase_type. */
    public var avgRightPowerPhase: List<Double?>?
        get() {
            val field = getField(AVG_RIGHT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgRightPowerPhase(it) }
        }
        set(value) {
            removeField(AVG_RIGHT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgRightPowerPhase(index, element) }
        }

    public fun getAvgRightPowerPhase(index: Int): Double? =
        getFieldValue(AVG_RIGHT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setAvgRightPowerPhase(index: Int, value: Double?) {
        setFieldValue(AVG_RIGHT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_right_power_phase_peak. */
    public val numAvgRightPowerPhasePeak: Int
        get() = getField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** avg_right_power_phase_peak, in degrees. Average right power phase peak angles. Data value indexes defined by power_phase_type. */
    public var avgRightPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgRightPowerPhasePeak(it) }
        }
        set(value) {
            removeField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgRightPowerPhasePeak(index, element) }
        }

    public fun getAvgRightPowerPhasePeak(index: Int): Double? =
        getFieldValue(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setAvgRightPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_power_position. */
    public val numAvgPowerPosition: Int
        get() = getField(AVG_POWER_POSITION_FIELD_NUM)?.numValues ?: 0

    /** avg_power_position, in watts. Average power by position. Data value indexes defined by rider_position_type. */
    public var avgPowerPosition: List<UShort?>?
        get() {
            val field = getField(AVG_POWER_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgPowerPosition(it) }
        }
        set(value) {
            removeField(AVG_POWER_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgPowerPosition(index, element) }
        }

    public fun getAvgPowerPosition(index: Int): UShort? =
        getFieldValue(AVG_POWER_POSITION_FIELD_NUM, index) as? UShort

    public fun setAvgPowerPosition(index: Int, value: UShort?) {
        setFieldValue(AVG_POWER_POSITION_FIELD_NUM, value, index)
    }

    /** Number of values stored in max_power_position. */
    public val numMaxPowerPosition: Int
        get() = getField(MAX_POWER_POSITION_FIELD_NUM)?.numValues ?: 0

    /** max_power_position, in watts. Maximum power by position. Data value indexes defined by rider_position_type. */
    public var maxPowerPosition: List<UShort?>?
        get() {
            val field = getField(MAX_POWER_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getMaxPowerPosition(it) }
        }
        set(value) {
            removeField(MAX_POWER_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMaxPowerPosition(index, element) }
        }

    public fun getMaxPowerPosition(index: Int): UShort? =
        getFieldValue(MAX_POWER_POSITION_FIELD_NUM, index) as? UShort

    public fun setMaxPowerPosition(index: Int, value: UShort?) {
        setFieldValue(MAX_POWER_POSITION_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_cadence_position. */
    public val numAvgCadencePosition: Int
        get() = getField(AVG_CADENCE_POSITION_FIELD_NUM)?.numValues ?: 0

    /** avg_cadence_position, in rpm. Average cadence by position. Data value indexes defined by rider_position_type. */
    public var avgCadencePosition: List<UByte?>?
        get() {
            val field = getField(AVG_CADENCE_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgCadencePosition(it) }
        }
        set(value) {
            removeField(AVG_CADENCE_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgCadencePosition(index, element) }
        }

    public fun getAvgCadencePosition(index: Int): UByte? =
        getFieldValue(AVG_CADENCE_POSITION_FIELD_NUM, index) as? UByte

    public fun setAvgCadencePosition(index: Int, value: UByte?) {
        setFieldValue(AVG_CADENCE_POSITION_FIELD_NUM, value, index)
    }

    /** Number of values stored in max_cadence_position. */
    public val numMaxCadencePosition: Int
        get() = getField(MAX_CADENCE_POSITION_FIELD_NUM)?.numValues ?: 0

    /** max_cadence_position, in rpm. Maximum cadence by position. Data value indexes defined by rider_position_type. */
    public var maxCadencePosition: List<UByte?>?
        get() {
            val field = getField(MAX_CADENCE_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getMaxCadencePosition(it) }
        }
        set(value) {
            removeField(MAX_CADENCE_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMaxCadencePosition(index, element) }
        }

    public fun getMaxCadencePosition(index: Int): UByte? =
        getFieldValue(MAX_CADENCE_POSITION_FIELD_NUM, index) as? UByte

    public fun setMaxCadencePosition(index: Int, value: UByte?) {
        setFieldValue(MAX_CADENCE_POSITION_FIELD_NUM, value, index)
    }

    /** manufacturer. Manufacturer that produced the segment */
    public var manufacturer: Manufacturer?
        get() = (getFieldValue(MANUFACTURER_FIELD_NUM) as? UShort)?.let { Manufacturer.fromValue(it) }
        set(value) = setFieldValue(MANUFACTURER_FIELD_NUM, value?.value)

    /** total_grit, in kGrit. The grit score estimates how challenging a route could be for a cyclist in terms of time spent going over sharp turns or large grade slopes. */
    public var totalGrit: Float?
        get() = getFieldValue(TOTAL_GRIT_FIELD_NUM) as? Float
        set(value) = setFieldValue(TOTAL_GRIT_FIELD_NUM, value)

    /** total_flow, in Flow. The flow score estimates how long distance wise a cyclist deaccelerates over intervals where deacceleration is unnecessary such as smooth turns or small grade angle intervals. */
    public var totalFlow: Float?
        get() = getFieldValue(TOTAL_FLOW_FIELD_NUM) as? Float
        set(value) = setFieldValue(TOTAL_FLOW_FIELD_NUM, value)

    /** avg_grit, in kGrit. The grit score estimates how challenging a route could be for a cyclist in terms of time spent going over sharp turns or large grade slopes. */
    public var avgGrit: Float?
        get() = getFieldValue(AVG_GRIT_FIELD_NUM) as? Float
        set(value) = setFieldValue(AVG_GRIT_FIELD_NUM, value)

    /** avg_flow, in Flow. The flow score estimates how long distance wise a cyclist deaccelerates over intervals where deacceleration is unnecessary such as smooth turns or small grade angle intervals. */
    public var avgFlow: Float?
        get() = getFieldValue(AVG_FLOW_FIELD_NUM) as? Float
        set(value) = setFieldValue(AVG_FLOW_FIELD_NUM, value)

    /** metabolic_calories, in kcal. */
    public var metabolicCalories: UShort?
        get() = getFieldValue(METABOLIC_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(METABOLIC_CALORIES_FIELD_NUM, value)

    /** total_fractional_ascent, in m. fractional part of total_ascent */
    public var totalFractionalAscent: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_ASCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_ASCENT_FIELD_NUM, value)

    /** total_fractional_descent, in m. fractional part of total_descent */
    public var totalFractionalDescent: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_DESCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_DESCENT_FIELD_NUM, value)

    /** enhanced_avg_altitude, in m. */
    public var enhancedAvgAltitude: Double?
        get() = getFieldValue(ENHANCED_AVG_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_AVG_ALTITUDE_FIELD_NUM, value)

    /** enhanced_max_altitude, in m. */
    public var enhancedMaxAltitude: Double?
        get() = getFieldValue(ENHANCED_MAX_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_MAX_ALTITUDE_FIELD_NUM, value)

    /** enhanced_min_altitude, in m. */
    public var enhancedMinAltitude: Double?
        get() = getFieldValue(ENHANCED_MIN_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_MIN_ALTITUDE_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val EVENT_FIELD_NUM: UByte = 0u
        public const val EVENT_TYPE_FIELD_NUM: UByte = 1u
        public const val START_TIME_FIELD_NUM: UByte = 2u
        public const val START_POSITION_LAT_FIELD_NUM: UByte = 3u
        public const val START_POSITION_LONG_FIELD_NUM: UByte = 4u
        public const val END_POSITION_LAT_FIELD_NUM: UByte = 5u
        public const val END_POSITION_LONG_FIELD_NUM: UByte = 6u
        public const val TOTAL_ELAPSED_TIME_FIELD_NUM: UByte = 7u
        public const val TOTAL_TIMER_TIME_FIELD_NUM: UByte = 8u
        public const val TOTAL_DISTANCE_FIELD_NUM: UByte = 9u
        public const val TOTAL_CYCLES_FIELD_NUM: UByte = 10u
        public const val TOTAL_CALORIES_FIELD_NUM: UByte = 11u
        public const val TOTAL_FAT_CALORIES_FIELD_NUM: UByte = 12u
        public const val AVG_SPEED_FIELD_NUM: UByte = 13u
        public const val MAX_SPEED_FIELD_NUM: UByte = 14u
        public const val AVG_HEART_RATE_FIELD_NUM: UByte = 15u
        public const val MAX_HEART_RATE_FIELD_NUM: UByte = 16u
        public const val AVG_CADENCE_FIELD_NUM: UByte = 17u
        public const val MAX_CADENCE_FIELD_NUM: UByte = 18u
        public const val AVG_POWER_FIELD_NUM: UByte = 19u
        public const val MAX_POWER_FIELD_NUM: UByte = 20u
        public const val TOTAL_ASCENT_FIELD_NUM: UByte = 21u
        public const val TOTAL_DESCENT_FIELD_NUM: UByte = 22u
        public const val SPORT_FIELD_NUM: UByte = 23u
        public const val EVENT_GROUP_FIELD_NUM: UByte = 24u
        public const val NEC_LAT_FIELD_NUM: UByte = 25u
        public const val NEC_LONG_FIELD_NUM: UByte = 26u
        public const val SWC_LAT_FIELD_NUM: UByte = 27u
        public const val SWC_LONG_FIELD_NUM: UByte = 28u
        public const val NAME_FIELD_NUM: UByte = 29u
        public const val NORMALIZED_POWER_FIELD_NUM: UByte = 30u
        public const val LEFT_RIGHT_BALANCE_FIELD_NUM: UByte = 31u
        public const val SUB_SPORT_FIELD_NUM: UByte = 32u
        public const val TOTAL_WORK_FIELD_NUM: UByte = 33u
        public const val AVG_ALTITUDE_FIELD_NUM: UByte = 34u
        public const val MAX_ALTITUDE_FIELD_NUM: UByte = 35u
        public const val GPS_ACCURACY_FIELD_NUM: UByte = 36u
        public const val AVG_GRADE_FIELD_NUM: UByte = 37u
        public const val AVG_POS_GRADE_FIELD_NUM: UByte = 38u
        public const val AVG_NEG_GRADE_FIELD_NUM: UByte = 39u
        public const val MAX_POS_GRADE_FIELD_NUM: UByte = 40u
        public const val MAX_NEG_GRADE_FIELD_NUM: UByte = 41u
        public const val AVG_TEMPERATURE_FIELD_NUM: UByte = 42u
        public const val MAX_TEMPERATURE_FIELD_NUM: UByte = 43u
        public const val TOTAL_MOVING_TIME_FIELD_NUM: UByte = 44u
        public const val AVG_POS_VERTICAL_SPEED_FIELD_NUM: UByte = 45u
        public const val AVG_NEG_VERTICAL_SPEED_FIELD_NUM: UByte = 46u
        public const val MAX_POS_VERTICAL_SPEED_FIELD_NUM: UByte = 47u
        public const val MAX_NEG_VERTICAL_SPEED_FIELD_NUM: UByte = 48u
        public const val TIME_IN_HR_ZONE_FIELD_NUM: UByte = 49u
        public const val TIME_IN_SPEED_ZONE_FIELD_NUM: UByte = 50u
        public const val TIME_IN_CADENCE_ZONE_FIELD_NUM: UByte = 51u
        public const val TIME_IN_POWER_ZONE_FIELD_NUM: UByte = 52u
        public const val REPETITION_NUM_FIELD_NUM: UByte = 53u
        public const val MIN_ALTITUDE_FIELD_NUM: UByte = 54u
        public const val MIN_HEART_RATE_FIELD_NUM: UByte = 55u
        public const val ACTIVE_TIME_FIELD_NUM: UByte = 56u
        public const val WKT_STEP_INDEX_FIELD_NUM: UByte = 57u
        public const val SPORT_EVENT_FIELD_NUM: UByte = 58u
        public const val AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 59u
        public const val AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 60u
        public const val AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 61u
        public const val AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 62u
        public const val AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 63u
        public const val STATUS_FIELD_NUM: UByte = 64u
        public const val UUID_FIELD_NUM: UByte = 65u
        public const val AVG_FRACTIONAL_CADENCE_FIELD_NUM: UByte = 66u
        public const val MAX_FRACTIONAL_CADENCE_FIELD_NUM: UByte = 67u
        public const val TOTAL_FRACTIONAL_CYCLES_FIELD_NUM: UByte = 68u
        public const val FRONT_GEAR_SHIFT_COUNT_FIELD_NUM: UByte = 69u
        public const val REAR_GEAR_SHIFT_COUNT_FIELD_NUM: UByte = 70u
        public const val TIME_STANDING_FIELD_NUM: UByte = 71u
        public const val STAND_COUNT_FIELD_NUM: UByte = 72u
        public const val AVG_LEFT_PCO_FIELD_NUM: UByte = 73u
        public const val AVG_RIGHT_PCO_FIELD_NUM: UByte = 74u
        public const val AVG_LEFT_POWER_PHASE_FIELD_NUM: UByte = 75u
        public const val AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 76u
        public const val AVG_RIGHT_POWER_PHASE_FIELD_NUM: UByte = 77u
        public const val AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 78u
        public const val AVG_POWER_POSITION_FIELD_NUM: UByte = 79u
        public const val MAX_POWER_POSITION_FIELD_NUM: UByte = 80u
        public const val AVG_CADENCE_POSITION_FIELD_NUM: UByte = 81u
        public const val MAX_CADENCE_POSITION_FIELD_NUM: UByte = 82u
        public const val MANUFACTURER_FIELD_NUM: UByte = 83u
        public const val TOTAL_GRIT_FIELD_NUM: UByte = 84u
        public const val TOTAL_FLOW_FIELD_NUM: UByte = 85u
        public const val AVG_GRIT_FIELD_NUM: UByte = 86u
        public const val AVG_FLOW_FIELD_NUM: UByte = 87u
        public const val METABOLIC_CALORIES_FIELD_NUM: UByte = 88u
        public const val TOTAL_FRACTIONAL_ASCENT_FIELD_NUM: UByte = 89u
        public const val TOTAL_FRACTIONAL_DESCENT_FIELD_NUM: UByte = 90u
        public const val ENHANCED_AVG_ALTITUDE_FIELD_NUM: UByte = 91u
        public const val ENHANCED_MAX_ALTITUDE_FIELD_NUM: UByte = 92u
        public const val ENHANCED_MIN_ALTITUDE_FIELD_NUM: UByte = 93u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("Event", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("EventType", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("StartTime", 2u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[3] = Field("StartPositionLat", 3u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[4] = Field("StartPositionLong", 4u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[5] = Field("EndPositionLat", 5u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[6] = Field("EndPositionLong", 6u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[7] = Field("TotalElapsedTime", 7u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[8] = Field("TotalTimerTime", 8u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[9] = Field("TotalDistance", 9u, BaseType.UINT32, 100.0, 0.0, "m", false)
            fields[10] = Field("TotalCycles", 10u, BaseType.UINT32, 1.0, 0.0, "cycles", false)
                .addSubField(
                    SubField("TotalStrokes", BaseType.UINT32, 1.0, 0.0, "strokes").also { subField ->
                        subField.addMap(23u, 2)
                    },
                )
            fields[11] = Field("TotalCalories", 11u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[12] = Field("TotalFatCalories", 12u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[13] = Field("AvgSpeed", 13u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
            fields[14] = Field("MaxSpeed", 14u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
            fields[15] = Field("AvgHeartRate", 15u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[16] = Field("MaxHeartRate", 16u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[17] = Field("AvgCadence", 17u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
            fields[18] = Field("MaxCadence", 18u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
            fields[19] = Field("AvgPower", 19u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[20] = Field("MaxPower", 20u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[21] = Field("TotalAscent", 21u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[22] = Field("TotalDescent", 22u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[23] = Field("Sport", 23u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[24] = Field("EventGroup", 24u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[25] = Field("NecLat", 25u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[26] = Field("NecLong", 26u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[27] = Field("SwcLat", 27u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[28] = Field("SwcLong", 28u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[29] = Field("Name", 29u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[30] = Field("NormalizedPower", 30u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[31] = Field("LeftRightBalance", 31u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[32] = Field("SubSport", 32u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[33] = Field("TotalWork", 33u, BaseType.UINT32, 1.0, 0.0, "J", false)
            fields[34] = Field("AvgAltitude", 34u, BaseType.UINT16, 5.0, 500.0, "m", false)
                .addComponent(FieldComponent(91u, false, 16, 5.0, 500.0))
            fields[35] = Field("MaxAltitude", 35u, BaseType.UINT16, 5.0, 500.0, "m", false)
                .addComponent(FieldComponent(92u, false, 16, 5.0, 500.0))
            fields[36] = Field("GpsAccuracy", 36u, BaseType.UINT8, 1.0, 0.0, "m", false)
            fields[37] = Field("AvgGrade", 37u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[38] = Field("AvgPosGrade", 38u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[39] = Field("AvgNegGrade", 39u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[40] = Field("MaxPosGrade", 40u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[41] = Field("MaxNegGrade", 41u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[42] = Field("AvgTemperature", 42u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[43] = Field("MaxTemperature", 43u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[44] = Field("TotalMovingTime", 44u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[45] = Field("AvgPosVerticalSpeed", 45u, BaseType.SINT16, 1000.0, 0.0, "m/s", false)
            fields[46] = Field("AvgNegVerticalSpeed", 46u, BaseType.SINT16, 1000.0, 0.0, "m/s", false)
            fields[47] = Field("MaxPosVerticalSpeed", 47u, BaseType.SINT16, 1000.0, 0.0, "m/s", false)
            fields[48] = Field("MaxNegVerticalSpeed", 48u, BaseType.SINT16, 1000.0, 0.0, "m/s", false)
            fields[49] = Field("TimeInHrZone", 49u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[50] = Field("TimeInSpeedZone", 50u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[51] = Field("TimeInCadenceZone", 51u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[52] = Field("TimeInPowerZone", 52u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[53] = Field("RepetitionNum", 53u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[54] = Field("MinAltitude", 54u, BaseType.UINT16, 5.0, 500.0, "m", false)
                .addComponent(FieldComponent(93u, false, 16, 5.0, 500.0))
            fields[55] = Field("MinHeartRate", 55u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[56] = Field("ActiveTime", 56u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[57] = Field("WktStepIndex", 57u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[58] = Field("SportEvent", 58u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[59] = Field("AvgLeftTorqueEffectiveness", 59u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[60] = Field("AvgRightTorqueEffectiveness", 60u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[61] = Field("AvgLeftPedalSmoothness", 61u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[62] = Field("AvgRightPedalSmoothness", 62u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[63] = Field("AvgCombinedPedalSmoothness", 63u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[64] = Field("Status", 64u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[65] = Field("Uuid", 65u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[66] = Field("AvgFractionalCadence", 66u, BaseType.UINT8, 128.0, 0.0, "rpm", false)
            fields[67] = Field("MaxFractionalCadence", 67u, BaseType.UINT8, 128.0, 0.0, "rpm", false)
            fields[68] = Field("TotalFractionalCycles", 68u, BaseType.UINT8, 128.0, 0.0, "cycles", false)
            fields[69] = Field("FrontGearShiftCount", 69u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[70] = Field("RearGearShiftCount", 70u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[71] = Field("TimeStanding", 71u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[72] = Field("StandCount", 72u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[73] = Field("AvgLeftPco", 73u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[74] = Field("AvgRightPco", 74u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[75] = Field("AvgLeftPowerPhase", 75u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[76] = Field("AvgLeftPowerPhasePeak", 76u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[77] = Field("AvgRightPowerPhase", 77u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[78] = Field("AvgRightPowerPhasePeak", 78u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[79] = Field("AvgPowerPosition", 79u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[80] = Field("MaxPowerPosition", 80u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[81] = Field("AvgCadencePosition", 81u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
            fields[82] = Field("MaxCadencePosition", 82u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
            fields[83] = Field("Manufacturer", 83u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[84] = Field("TotalGrit", 84u, BaseType.FLOAT32, 1.0, 0.0, "kGrit", false)
            fields[85] = Field("TotalFlow", 85u, BaseType.FLOAT32, 1.0, 0.0, "Flow", false)
            fields[86] = Field("AvgGrit", 86u, BaseType.FLOAT32, 1.0, 0.0, "kGrit", false)
            fields[87] = Field("AvgFlow", 87u, BaseType.FLOAT32, 1.0, 0.0, "Flow", false)
            fields[88] = Field("MetabolicCalories", 88u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[89] = Field("TotalFractionalAscent", 89u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[90] = Field("TotalFractionalDescent", 90u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[91] = Field("EnhancedAvgAltitude", 91u, BaseType.UINT32, 5.0, 500.0, "m", false)
            fields[92] = Field("EnhancedMaxAltitude", 92u, BaseType.UINT32, 5.0, 500.0, "m", false)
            fields[93] = Field("EnhancedMinAltitude", 93u, BaseType.UINT32, 5.0, 500.0, "m", false)
            return fields
        }
    }
}
